import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiGet } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import SimpleUrlInput from "../components/SimpleUrlInput";
import { useToast } from "../context/ToastContext";
import { isValidWebsiteUrl } from "../utils/urlValidation";
import { formatDateOnly } from "../utils/dateTime";

function scoreColor(score) {
  if (score == null) return "var(--text-muted)";
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "#84cc16";
  if (score >= 40) return "var(--warning)";
  return "var(--danger)";
}

function ScorePill({ score }) {
  if (score == null) return <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>—</span>;
  return (
    <span
      style={{
        fontWeight: 700,
        color: scoreColor(score),
        fontSize: "0.875rem",
      }}
    >
      {score}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="card dashboard-kpi" style={{ padding: "16px 20px" }}>
      <div className="dashboard-kpi-label">{label}</div>
      <div className="dashboard-kpi-value" style={{ fontSize: "1.5rem" }}>{value ?? "—"}</div>
      {sub ? <div className="dashboard-kpi-meta">{sub}</div> : null}
    </div>
  );
}

const PAGE_SIZE = 20;

function normalizeDomainInput(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";

  let withoutProtocol = trimmed.replace(/^https?:\/\//i, "");
  withoutProtocol = withoutProtocol.replace(/^\/+/, "");
  const slashIndex = withoutProtocol.indexOf("/");
  const hostOnly = slashIndex >= 0 ? withoutProtocol.slice(0, slashIndex) : withoutProtocol;
  return hostOnly.trim().replace(/^www\./i, "");
}

export default function DomainHistory() {
  const { showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const domain = searchParams.get("domain") || "";

  const [domainInput, setDomainInput] = useState(domain);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const fetchDomain = useCallback(
    async (targetDomain, targetPage = 1) => {
      if (!targetDomain) return;
      setLoading(true);
      try {
        const { data: res } = await apiGet("/api/seo/audit-history/domain", {
          domain: targetDomain,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setData(res);
        setPage(targetPage);
      } catch (err) {
        showError(parseApiError(err, "Failed to load domain history"));
      } finally {
        setLoading(false);
      }
    },
    [showError]
  );

  useEffect(() => {
    if (domain) {
      setDomainInput(domain);
      fetchDomain(domain);
    }
  }, [domain, fetchDomain]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!isValidWebsiteUrl(domainInput)) {
      showError("Please enter a valid URL.");
      return;
    }
    const normalizedDomain = normalizeDomainInput(domainInput);
    if (!normalizedDomain) return;
    setDomainInput(`https://${normalizedDomain}`);
    setSearchParams({ domain: normalizedDomain });
    fetchDomain(normalizedDomain);
  };

  const stats = data?.stats;
  const tableRows = (data?.data || []).filter((audit) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || audit.url?.toLowerCase().includes(q) || audit.title?.toLowerCase().includes(q);
    const score = audit.seo_score ?? 0;
    const matchesScore =
      scoreFilter === "all" ||
      (scoreFilter === "excellent" && score >= 80) ||
      (scoreFilter === "good" && score >= 60 && score < 80) ||
      (scoreFilter === "fair" && score >= 40 && score < 60) ||
      (scoreFilter === "poor" && score < 40);
    const createdAt = new Date(audit.created_at);
    const fromOk = !dateFrom || createdAt >= new Date(`${dateFrom}T00:00:00`);
    const toOk = !dateTo || createdAt <= new Date(`${dateTo}T23:59:59`);
    return matchesSearch && matchesScore && fromOk && toOk;
  }).sort((a, b) => {
    const aDate = new Date(a.created_at).getTime();
    const bDate = new Date(b.created_at).getTime();
    const aScore = Number(a.seo_score) || 0;
    const bScore = Number(b.seo_score) || 0;
    if (sortBy === "oldest") return aDate - bDate;
    if (sortBy === "highest-score") return bScore - aScore;
    if (sortBy === "lowest-score") return aScore - bScore;
    return bDate - aDate;
  });

  return (
    <div className="page">
      <h1 className="page-title">Domain History</h1>
      <p className="dashboard-lead" style={{ marginTop: "-8px" }}>
        View all SEO audits across every page on a domain, with aggregate health stats.
      </p>

      {/* Domain search */}
      <div className="card" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSearch}>
          <div className="input-group">
            <SimpleUrlInput
              value={domainInput}
              onChange={setDomainInput}
              placeholder="your-website.com"
              aria-label="Domain to view history"
            />
            <button type="submit" className="btn btn-primary" disabled={loading || !domainInput.trim()}>
              {loading ? "Loading..." : "View Domain"}
            </button>
          </div>
        </form>
      </div>

      {loading ? (
        <div className="card skeleton-table">
          <div className="skeleton-block skeleton-row" />
          <div className="skeleton-block skeleton-row" />
          <div className="skeleton-block skeleton-row" />
        </div>
      ) : null}

      {data && !loading && (
        <>
          {/* Domain stats */}
          <h2 className="page-subtitle" style={{ marginBottom: 12 }}>
            {data.domain}
            <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: "0.9rem", marginLeft: 8 }}>
              — {stats?.total_audits ?? 0} audit{stats?.total_audits !== 1 ? "s" : ""} across all pages
            </span>
          </h2>

          {stats && (
            <div className="dashboard-kpis" style={{ marginBottom: 24 }}>
              <StatCard
                label="Avg SEO Score"
                value={stats.avg_score ? `${Math.round(stats.avg_score)}/100` : "—"}
                sub="Across all audited pages"
              />
              <StatCard
                label="Best Page Score"
                value={stats.best_score != null ? `${stats.best_score}/100` : "—"}
                sub="Highest scored page"
              />
              <StatCard
                label="Worst Page Score"
                value={stats.worst_score != null ? `${stats.worst_score}/100` : "—"}
                sub="Most needs attention"
              />
            </div>
          )}

          <div style={{ marginBottom: "18px", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="text"
              placeholder="Search by page URL or title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
              style={{ flex: "1", minWidth: "240px" }}
            />
            <select className="input" value={scoreFilter} onChange={(e) => setScoreFilter(e.target.value)} style={{ minWidth: "150px" }}>
              <option value="all">All scores</option>
              <option value="excellent">Excellent (80+)</option>
              <option value="good">Good (60-79)</option>
              <option value="fair">Fair (40-59)</option>
              <option value="poor">Poor (&lt;40)</option>
            </select>
            <input type="date" className="input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ minWidth: "165px" }} />
            <input type="date" className="input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ minWidth: "165px" }} />
            <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ minWidth: "190px" }}>
              <option value="newest">Sort: Newest</option>
              <option value="oldest">Sort: Oldest</option>
              <option value="highest-score">Sort: Highest score</option>
              <option value="lowest-score">Sort: Lowest score</option>
            </select>
          </div>

          {tableRows.length === 0 ? (
            <div className="empty-state">
              No records match this domain/filter combination. Run a <Link to="/">SEO audit</Link> to get started.
            </div>
          ) : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Page URL</th>
                      <th>Title</th>
                      <th>SEO Score</th>
                      <th>H1</th>
                      <th>Images</th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((audit) => (
                      <tr key={audit.id}>
                        <td
                          style={{
                            maxWidth: 260,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={audit.url}
                        >
                          <a href={audit.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.8rem" }}>
                            {audit.url.replace(/^https?:\/\/(www\.)?/, "")}
                          </a>
                        </td>
                        <td
                          style={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={audit.title}
                        >
                          {audit.title && audit.title !== "No title" ? audit.title : (
                            <span style={{ color: "var(--danger)", fontSize: "0.8rem" }}>Missing title</span>
                          )}
                        </td>
                        <td>
                          <ScorePill score={audit.seo_score} />
                        </td>
                        <td>
                          {audit.h1 && audit.h1 !== "No H1" ? (
                            <span style={{ fontSize: "0.8rem" }}>{audit.h1.slice(0, 40)}{audit.h1.length > 40 ? "..." : ""}</span>
                          ) : (
                            <span style={{ color: "var(--danger)", fontSize: "0.8rem" }}>Missing</span>
                          )}
                        </td>
                        <td style={{ fontSize: "0.8rem" }}>
                          {(() => {
                            try {
                              const imgs = typeof audit.images === "string" ? JSON.parse(audit.images) : audit.images;
                              const missingAlt = imgs?.missingAlt ?? 0;
                              return (
                                <span style={{ color: missingAlt > 0 ? "var(--warning)" : "inherit" }}>
                                  {imgs?.total ?? 0} ({missingAlt} no alt)
                                </span>
                              );
                            } catch {
                              return "—";
                            }
                          })()}
                        </td>
                        <td style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                          {formatDateOnly(audit.created_at)}
                        </td>
                        <td>
                          <span className={`pill-badge ${(audit.seo_score ?? 0) >= 60 ? "ok" : "alert"}`}>
                            {(audit.seo_score ?? 0) >= 60 ? "Healthy" : "Alert"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.pagination && data.pagination.totalPages > 1 ? (
                <div className="pagination" style={{ marginTop: 20 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={page <= 1}
                    onClick={() => fetchDomain(data.domain, page - 1)}
                  >
                    {"<- Prev"}
                  </button>
                  <span className="pagination-info">
                    Page {data.pagination.page} of {data.pagination.totalPages} — {data.pagination.total} pages audited
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={page >= data.pagination.totalPages}
                    onClick={() => fetchDomain(data.domain, page + 1)}
                  >
                    {"Next ->"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
